import React, { useRef, useState } from "react";
import { ABtn, AResult, AScore } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";
import { useInput, useLoop, Joystick, Token, dist, clamp } from "./arena";

const CFG = {
  pakdam: { total: 6, time: 38, ps: 34, ns: 22, dodge: 0.9, mode: "remove", hint: "Catch every kid before time runs out!", verb: "caught", win: "Sab pakde! 🎉" },
  khokho: { total: 6, time: 40, ps: 33, ns: 31, dodge: 2.0, mode: "remove", dash: true, hint: "Chase the darting runners — they juke hard! SPACE / DASH = kho burst!", verb: "tagged", win: "Kho-Kho jeet! 🏃" },
  langdi: { total: 5, time: 44, ps: 40, ns: 22, dodge: 0.7, mode: "remove", stamina: true, hint: "Hop & tag! Moving drains stamina — rest to refill.", verb: "caught", win: "Ek taang pe kamaal! 🦵" },
  chain: { total: 6, time: 44, ps: 34, ns: 24, dodge: 1.1, mode: "chain", hint: "Catch kids — they join your chain. Catch them all!", verb: "in chain", win: "Poori chain ban gayi! ⛓️" },
  baraf: { total: 6, time: 44, ps: 34, ns: 26, dodge: 1.4, mode: "freeze", hint: "Tag everyone to FREEZE (Baraf!). Freeze all to win.", verb: "frozen", win: "Sab baraf! 🧊" },
  vish: { total: 6, time: 50, ps: 35, ns: 26, dodge: 1.5, mode: "freeze", reviver: true, hint: "You're Vish — freeze all. Amrit keeps reviving them!", verb: "frozen", win: "Sab ko Vish! ☠️" },
};

const rnd = (a, b) => a + Math.random() * (b - a);

export default function TagArena({ mode = "pakdam" }) {
  const cfg = CFG[mode];
  const [phase, setPhase] = useState("ready");
  const [tick, setTick] = useState(0);
  const [time, setTime] = useState(cfg.time);
  const [stam, setStam] = useState(100);
  const [caught, setCaught] = useState(0);
  const player = useRef({ x: 50, y: 50 });
  const npcs = useRef([]);
  const trail = useRef([]);
  const reviveT = useRef(0);
  const dashT = useRef(0);
  const { dir, keys, setJoy } = useInput();

  const spawn = () => {
    player.current = { x: 50, y: 80 };
    trail.current = [];
    npcs.current = Array.from({ length: cfg.total }).map((_, i) => ({
      id: i, x: rnd(10, 90), y: rnd(10, 55), vx: rnd(-1, 1), vy: rnd(-1, 1),
      wander: rnd(0, 6.28), side: Math.random() < 0.5 ? 1 : -1,
      out: false, frozen: false, chain: -1,
    }));
  };

  const start = () => { spawn(); setTime(cfg.time); setStam(100); setCaught(0); reviveT.current = 0; dashT.current = 0; setPhase("play"); };

  useLoop(phase === "play", (dt) => {
    const p = player.current;
    let d = dir();
    // stamina / dash
    let sp = cfg.ps;
    if (cfg.stamina) {
      const moving = Math.hypot(d.x, d.y) > 0.1;
      setStam((s) => {
        let ns = moving ? s - 28 * dt : s + 20 * dt;
        ns = clamp(ns, 0, 100);
        if (moving && s <= 0) { d = { x: 0, y: 0 }; }
        return ns;
      });
      if (stam <= 1) d = { x: 0, y: 0 };
    }
    if (cfg.dash) {
      dashT.current -= dt;
      if ((keys.current[" "] || keys.current["dash"]) && dashT.current <= 0) dashT.current = 1.0;
      if (dashT.current > 0.4) sp = cfg.ps * 1.9;
    }
    p.x = clamp(p.x + d.x * sp * dt, 4, 96);
    p.y = clamp(p.y + d.y * sp * dt, 4, 96);
    trail.current.unshift({ x: p.x, y: p.y });
    if (trail.current.length > 120) trail.current.pop();

    let cc = 0, chainN = 0;
    npcs.current.forEach((n) => {
      if (cfg.mode === "chain" && n.chain >= 0) {
        const t = trail.current[Math.min(trail.current.length - 1, (n.chain + 1) * 10)];
        if (t) { n.x = t.x; n.y = t.y; }
        chainN++;
        return;
      }
      if (n.out || n.frozen) { if (n.out) cc++; if (n.frozen) cc++; return; }
      // steering: flee from player + avoid walls + wander + juke (tangential dodge)
      let fx = n.x - p.x, fy = n.y - p.y;
      const pd = Math.hypot(fx, fy) || 1;
      fx /= pd; fy /= pd;
      const fleeW = clamp(1 - pd / 45, 0, 1) * 1.8;          // flee harder only when player is near
      // wall repulsion keeps them off edges & out of corners
      const mg = 20;
      const wx = Math.max(0, mg - n.x) / mg - Math.max(0, n.x - (100 - mg)) / mg;
      const wy = Math.max(0, mg - n.y) / mg - Math.max(0, n.y - (100 - mg)) / mg;
      // wander so they roam the whole ground
      n.wander += rnd(-0.5, 0.5);
      const wanderX = Math.cos(n.wander) * 0.7, wanderY = Math.sin(n.wander) * 0.7;
      // juke: dart perpendicular to the player when close (dodge past)
      let tx = 0, ty = 0;
      if (pd < 28) { tx = -fy * n.side; ty = fx * n.side; if (Math.random() < 0.02) n.side *= -1; }
      const dx = fx * fleeW + wx * 2.4 + wanderX + tx * (cfg.dodge || 1);
      const dy = fy * fleeW + wy * 2.4 + wanderY + ty * (cfg.dodge || 1);
      const dm = Math.hypot(dx, dy) || 1;
      n.vx = n.vx * 0.75 + (dx / dm) * 0.25;
      n.vy = n.vy * 0.75 + (dy / dm) * 0.25;
      n.x = clamp(n.x + n.vx * cfg.ns * dt, 3, 97);
      n.y = clamp(n.y + n.vy * cfg.ns * dt, 3, 97);
      // tag?
      if (dist(p, n) < 5.5) {
        sfx.hit?.();
        if (cfg.mode === "remove") { n.out = true; }
        else if (cfg.mode === "freeze") { n.frozen = true; }
        else if (cfg.mode === "chain") { n.chain = chainN; }
      }
    });

    if (cfg.reviver) {
      reviveT.current += dt;
      if (reviveT.current > 2.6) {
        reviveT.current = 0;
        const frz = npcs.current.filter((n) => n.frozen);
        if (frz.length && frz.length < cfg.total) frz[Math.floor(Math.random() * frz.length)].frozen = false;
      }
    }

    const done = cfg.mode === "chain"
      ? npcs.current.every((n) => n.chain >= 0)
      : npcs.current.every((n) => n.out || n.frozen);
    setCaught(cfg.mode === "chain" ? npcs.current.filter((n) => n.chain >= 0).length : npcs.current.filter((n) => n.out || n.frozen).length);

    setTime((t) => {
      const nt = t - dt;
      if (done) { setPhase("win"); sfx.win?.(); return t; }
      if (nt <= 0) { setPhase("lose"); return 0; }
      return nt;
    });
    setTick((x) => (x + 1) % 100000);
  });

  const list = npcs.current;
  return (
    <div className="w-full text-center">
      <div className="flex flex-wrap justify-center gap-2">
        <AScore label="Time" value={`${Math.ceil(time)}s`} />
        <AScore label={cfg.verb} value={`${caught}/${cfg.total}`} />
        {cfg.stamina && <AScore label="Stamina" value={`${Math.round(stam)}%`} />}
      </div>
      <p className="my-2 text-sm text-kirana-brown/70">{cfg.hint}</p>

      <div data-testid="tag-arena" className="relative mx-auto mb-3 h-72 w-full max-w-md overflow-hidden rounded-md border-2 border-kirana-border bg-gradient-to-b from-[#9ed17d] to-[#6fa354]">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "repeating-linear-gradient(0deg,#3f7d3f 0 22px,transparent 22px 46px)" }} />
        {phase !== "ready" && <Token testid="tag-player" x={player.current.x} y={player.current.y} color="#1D6FB8" label="😀" size={34} z={8} />}
        {phase !== "ready" && list.map((n) => (
          <Token key={n.id} x={n.x} y={n.y}
            color={n.frozen ? "#8ecae6" : n.chain >= 0 ? "#2A9D8F" : "#E63946"}
            label={n.frozen ? "🥶" : n.out ? "" : "🧒"} dim={n.out}
            size={n.out ? 0 : 28} />
        ))}
        {cfg.stamina && (
          <div className="absolute bottom-2 left-2 h-2 w-28 overflow-hidden rounded-full bg-black/25">
            <div className="h-full bg-kirana-gold transition-all" style={{ width: `${stam}%` }} />
          </div>
        )}
      </div>

      {phase === "play" && (
        <div className="flex items-center justify-center gap-4">
          <Joystick onMove={setJoy} />
          <span className="hidden text-xs text-kirana-brown/60 sm:inline">Move: WASD / Arrow keys{cfg.dash ? " · Space = Dash" : ""}</span>
          {cfg.dash && <ABtn data-testid="tag-dash" onClick={() => { dashT.current = 1.0; }} tone="gold" className="px-5 py-3 sm:hidden">DASH</ABtn>}
        </div>
      )}

      <div className="mt-3">
        {phase === "ready" && <ABtn data-testid="tag-start" onClick={start} tone="gold" className="px-8 py-3">Start</ABtn>}
        {(phase === "win" || phase === "lose") && (
          <>
            <AResult testId="tag-result" win={phase === "win"} text={phase === "win" ? cfg.win : `Time up! ${caught}/${cfg.total} ${cfg.verb}.`} />
            <div className="mt-3 flex justify-center"><ABtn data-testid="tag-again" onClick={start} tone="green">Play Again</ABtn></div>
          </>
        )}
      </div>
    </div>
  );
}
